import React, { FunctionComponent, useState } from 'react';
import { useDispatch } from 'react-redux';
import { LocalizationProvider, MobileDatePicker } from '@mui/lab';
import {
  Autocomplete,
  Checkbox,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  Stack,
  TextFieldProps,
  Typography,
} from '@mui/material';
import { Close as CloseIcon, Delete as DeleteIcon } from '@mui/icons-material';
import moment, { Moment } from 'moment';
import AdapterMoment from '@mui/lab/AdapterMoment';

import DarkLoadingButton, {
  DarkButton,
  defaultButtonStyles,
} from '../../../../../../components/V2/Buttons';
import UserListDropdown, {
  TUserSelectOption,
} from '../../../../../../components/Inputs/admin/UserListDropdown';
import {
  TPAutoCompleteArrowIconComponent,
  TPTextField,
} from '../../../../../../components/Inputs';
import {
  currencyOptions,
  flexiblePlanExpiredInTypeOptions,
  ICurrencyOption,
  TFlexiblePlanExpiredInTypeOption,
} from '../../../../../../constants';
import {
  adminCreateStripeInvoice,
  TAdminCreateStripeInvoice,
} from '../../../../../../store/V2/InvoicesSlice';
import AddCustomItemModal from './AddCustomItemModal';
import { capitalizeFirstLetter } from '../../../../../../helper';

interface SendInvoiceModalProps {
  open: boolean;
  onClose: () => void;
}

type TCustomItem = {
  _id: string;
  description: string;
  quantity: number;
  rate: number;
};

type TState = {
  selectedUser: TUserSelectOption | null;
  selectedCurrency: ICurrencyOption | null;
  isCreditChecked: boolean;
  creditQuantity: string;
  creditRate: string;
  creditDescription: string;
  isCustomChecked: boolean;
  customItems: TCustomItem[];
  expiryDate: Moment | null;
  expiredInCount: string;
  expiredInType: TFlexiblePlanExpiredInTypeOption;
};

const textFieldProps: TextFieldProps = {
  sx: {
    '& .MuiInputBase-root': {
      height: '52px',
    },
  },
  InputLabelProps: {
    shrink: true,
  },
};

const initialState: TState = {
  selectedUser: null,
  selectedCurrency: null,
  isCreditChecked: false,
  creditQuantity: '',
  creditRate: '',
  creditDescription: 'Credit Purchasing',
  isCustomChecked: false,
  customItems: [],
  expiryDate: moment().add(3, 'days').startOf('day'),
  expiredInCount: '1',
  expiredInType: flexiblePlanExpiredInTypeOptions[3],
};

const SendInvoiceModal: FunctionComponent<SendInvoiceModalProps> = ({
  open,
  onClose,
}) => {
  const dispatch = useDispatch();

  // State
  const [state, setState] = useState({ ...initialState });
  const [isLoading, setLoading] = useState(false);
  const [
    isAddCustomItemItemModalOpen,
    setAddCustomItemItemModalOpen,
  ] = useState(false);

  const isSubmitDisabled = Boolean(
    !state.selectedUser ||
      !state.selectedCurrency ||
      (state.isCreditChecked && !state.creditQuantity.trim().length) ||
      (state.isCreditChecked && parseFloat(state.creditQuantity) < 1) ||
      (state.isCreditChecked && !state.creditRate.trim().length) ||
      (state.isCreditChecked && parseFloat(state.creditRate) < 1) ||
      (state.isCustomChecked && !state.customItems.length) ||
      (state.isCreditChecked && !state.expiredInCount.trim().length) ||
      (state.isCreditChecked &&
        state.expiredInCount &&
        parseFloat(state.expiredInCount) < 1)
  );

  let totalAmount = 0;
  let totalAmountText = '';

  if (state.selectedCurrency) {
    totalAmountText = state.selectedCurrency.value;

    if (state.creditQuantity && state.creditRate) {
      totalAmount +=
        parseFloat(state.creditQuantity) * parseFloat(state.creditRate);
    }

    if (state.customItems.length) {
      state.customItems.forEach((item) => {
        totalAmount += item.quantity * item.rate;
      });
    }
  }

  totalAmountText += ` ${parseFloat(totalAmount.toFixed(2))}`;

  const updateStateAttributes = (attributes: Partial<TState>) => {
    setState((prevState) => ({
      ...prevState,
      ...attributes,
    }));
  };

  const addCustomItem = (customItem: TCustomItem) => {
    setState((prevState) => {
      const newStateData = {
        ...prevState,
        customItems: [...prevState.customItems, customItem],
      };

      return newStateData;
    });
  };

  const removeCustomItem = (customItem: TCustomItem) => {
    setState((prevState) => {
      const newStateData = {
        ...prevState,
        customItems: [...prevState.customItems].filter(
          (item) => item._id !== customItem._id
        ),
      };

      return newStateData;
    });
  };

  const handleSubmit = async () => {
    setLoading(true);

    if (state.selectedUser && state.selectedCurrency && state.expiryDate) {
      const apiData: TAdminCreateStripeInvoice = {
        userId: state.selectedUser.value,
        currency: state.selectedCurrency.value,
        isCreditChecked: state.isCreditChecked,
        isCustomChecked: state.isCustomChecked,
        expiryDate: state.expiryDate.clone().startOf('day').toISOString(),
      };

      if (state.isCreditChecked && state.creditQuantity && state.creditRate) {
        apiData.creditQuantity = parseFloat(state.creditQuantity);
        apiData.creditRate = parseFloat(state.creditRate);
        apiData.creditDescription = state.creditDescription;
        apiData.expiredIn = {
          count: parseFloat(state.expiredInCount),
          type: state.expiredInType.value,
        };
      }

      if (state.isCustomChecked) {
        apiData.customAmounts = state.customItems.map((item) => ({
          description: item.description,
          quantity: item.quantity,
          rate: item.rate,
        }));
      }

      const res: any = await dispatch(adminCreateStripeInvoice(apiData));

      if (res.payload) {
        onClose();
      }
    }

    setLoading(false);
  };

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { borderRadius: '20px' },
      }}
    >
      <DialogTitle
        sx={{
          m: 0,
          p: { xs: '15px', sm: '30px' },
          color: '#000000',
          fontWeight: 600,
          fontSize: '16px',
        }}
      >
        Send Invoice
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{
          position: 'absolute',
          right: { xs: '7px', sm: '22px' },
          top: { xs: '7px', sm: '22px' },
        }}
      >
        <CloseIcon sx={{ color: '#000000' }} />
      </IconButton>
      <DialogContent
        sx={{ p: { xs: '0 15px 15px 15px', sm: '0 30px 30px 30px' } }}
      >
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <div style={{ paddingTop: '6px' }}>
            <Grid container spacing="20px">
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <UserListDropdown
                    selectedItem={state.selectedUser}
                    handleChange={(event, value) =>
                      updateStateAttributes({ selectedUser: value })
                    }
                    textFieldProps={{
                      label: 'User *',
                      placeholder: 'Select',
                      ...textFieldProps,
                    }}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <Autocomplete
                    fullWidth
                    size="small"
                    value={state.selectedCurrency}
                    options={currencyOptions}
                    popupIcon={TPAutoCompleteArrowIconComponent}
                    onChange={(event, value) =>
                      updateStateAttributes({
                        selectedCurrency: value,
                      })
                    }
                    renderInput={(params) => (
                      <TPTextField
                        {...params}
                        {...textFieldProps}
                        size="small"
                        label="Currency *"
                        placeholder="Select"
                      />
                    )}
                  />
                </FormControl>
              </Grid>
              {state.selectedUser && state.selectedCurrency ? (
                <>
                  <Grid item xs={12}>
                    <FormGroup>
                      <FormControlLabel
                        label="Credit"
                        sx={{ width: 'fit-content' }}
                        control={
                          <Checkbox
                            checked={state.isCreditChecked}
                            onChange={(event, value) =>
                              updateStateAttributes({ isCreditChecked: value })
                            }
                          />
                        }
                      />
                      <Collapse in={state.isCreditChecked}>
                        <Stack sx={{ pt: '20px', pb: '20px' }} gap="20px">
                          <div>
                            <TPTextField
                              {...textFieldProps}
                              fullWidth
                              type="number"
                              label="Quantity (Hours) *"
                              placeholder="Enter here..."
                              value={state.creditQuantity}
                              onChange={(event) =>
                                updateStateAttributes({
                                  creditQuantity: event.target.value,
                                })
                              }
                            />
                          </div>
                          <div>
                            <TPTextField
                              {...textFieldProps}
                              fullWidth
                              type="number"
                              label="Hourly Rate *"
                              placeholder="Enter here..."
                              value={state.creditRate}
                              onChange={(event) =>
                                updateStateAttributes({
                                  creditRate: event.target.value,
                                })
                              }
                            />
                          </div>
                          <div>
                            <TPTextField
                              multiline
                              fullWidth
                              rows={3}
                              label="Description *"
                              placeholder="Enter here..."
                              value={state.creditDescription}
                              InputLabelProps={{ shrink: true }}
                              onChange={(event) =>
                                updateStateAttributes({
                                  creditDescription: event.target.value,
                                })
                              }
                            />
                          </div>
                          <div>
                            <Typography fontSize="14px" fontWeight={600}>
                              Expired In *
                            </Typography>
                          </div>
                          <Grid container spacing={2}>
                            <Grid item xs={6}>
                              <FormControl fullWidth>
                                <TPTextField
                                  {...textFieldProps}
                                  type="number"
                                  placeholder="Enter here..."
                                  value={state.expiredInCount}
                                  onChange={(event) =>
                                    updateStateAttributes({
                                      expiredInCount: event.target.value,
                                    })
                                  }
                                />
                              </FormControl>
                            </Grid>
                            <Grid item xs={6}>
                              <FormControl fullWidth>
                                <Autocomplete
                                  disableClearable
                                  fullWidth
                                  size="small"
                                  value={state.expiredInType}
                                  options={flexiblePlanExpiredInTypeOptions}
                                  popupIcon={TPAutoCompleteArrowIconComponent}
                                  onChange={(event, value) =>
                                    updateStateAttributes({
                                      expiredInType: value,
                                    })
                                  }
                                  getOptionLabel={(option) =>
                                    capitalizeFirstLetter(
                                      option.label.toLocaleLowerCase()
                                    )
                                  }
                                  renderInput={(params) => (
                                    <TPTextField
                                      {...params}
                                      {...textFieldProps}
                                      size="small"
                                      placeholder="Type"
                                    />
                                  )}
                                />
                              </FormControl>
                            </Grid>
                          </Grid>
                        </Stack>
                      </Collapse>
                      <FormControlLabel
                        label="Custom"
                        sx={{ width: 'fit-content' }}
                        control={
                          <Checkbox
                            checked={state.isCustomChecked}
                            onChange={(event, value) =>
                              updateStateAttributes({ isCustomChecked: value })
                            }
                          />
                        }
                      />
                      <Collapse in={state.isCustomChecked}>
                        <Stack sx={{ pt: '20px', pb: '20px' }} gap="20px">
                          <div>
                            <Typography fontSize="14px" fontWeight={600}>
                              Custom Items
                            </Typography>
                          </div>
                          <Stack direction="row" justifyContent="flex-end">
                            <DarkButton
                              sx={{ ...defaultButtonStyles }}
                              onClick={() =>
                                setAddCustomItemItemModalOpen(true)
                              }
                            >
                              Add New
                            </DarkButton>
                          </Stack>
                          {state.customItems.length ? (
                            <Stack gap="10px">
                              {state.customItems.map((customItem) => {
                                return (
                                  <Stack
                                    direction="row"
                                    alignItems="center"
                                    justifyContent="space-between"
                                    key={customItem._id}
                                  >
                                    <Typography
                                      fontSize="14px"
                                      fontWeight={500}
                                    >
                                      {customItem.description} - (
                                      {state.selectedCurrency?.value}{' '}
                                      {parseFloat(customItem.rate.toFixed(2))} x{' '}
                                      {customItem.quantity})
                                    </Typography>
                                    <IconButton
                                      onClick={() =>
                                        removeCustomItem(customItem)
                                      }
                                    >
                                      <DeleteIcon sx={{ color: '#1E293B' }} />
                                    </IconButton>
                                  </Stack>
                                );
                              })}
                            </Stack>
                          ) : null}
                        </Stack>
                      </Collapse>
                    </FormGroup>
                  </Grid>
                </>
              ) : null}
              <Grid item xs={12}>
                <Divider sx={{ borderStyle: 'dashed' }} />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <MobileDatePicker
                    clearable
                    value={state.expiryDate}
                    label="Expiry Date *"
                    inputFormat="YYYY-MM-DD"
                    onChange={(date) =>
                      updateStateAttributes({
                        expiryDate: date,
                      })
                    }
                    renderInput={(params) => (
                      <TPTextField
                        {...params}
                        {...textFieldProps}
                        size="small"
                        placeholder="Select"
                      />
                    )}
                  />
                </FormControl>
              </Grid>
            </Grid>
          </div>
        </LocalizationProvider>
        {/* MODALS */}
        {isAddCustomItemItemModalOpen ? (
          <AddCustomItemModal
            open={isAddCustomItemItemModalOpen}
            onClose={() => setAddCustomItemItemModalOpen(false)}
            onSubmit={addCustomItem}
          />
        ) : null}
      </DialogContent>
      <DialogActions
        sx={{
          p: { xs: '15px', sm: '30px' },
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography fontWeight={600}>Total - {totalAmountText}</Typography>
        <DarkLoadingButton
          disableElevation
          variant="contained"
          color="inherit"
          loading={isLoading}
          onClick={handleSubmit}
          disabled={isSubmitDisabled}
          sx={{ ...defaultButtonStyles }}
        >
          Send
        </DarkLoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default SendInvoiceModal;
